import React, { useEffect, useMemo, useState } from "react";

import { useLocationsContext } from "domain/Location";
import { useCategoriesContext } from "domain/Category";
import { useCompaniesContext } from "domain/Company";
import { useEmployeesContext } from "domain/Employee";
import { Button, Dropdown, InputSwitch, SearchBar } from "shared";
import { BsFilter } from "react-icons/bs";
import { IoClose } from "react-icons/io5";

import { useItemsFilters } from "../../hooks";

import "./filters-group.scss";
import { useFiltersContext } from "../../context";
import { SortingGroup, useSortingContext } from "domain/Sorting";
import clsx from "clsx";
import { common, sortingOptions } from "shared/constants/constants";
import { useInvoicesContext } from "domain/Invoice";

export const FiltersGroup = ({ hideUnassignedSwitch = false }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [clearChoices, setClearChoices] = useState(false);
  const { applyFilters, selections, applyPendingFilters, clearFilters } = useFiltersContext();
  const { applySortingHandler, clearSorting } = useSortingContext();
  const { handleCategoriesSearch, categoriesFilter } = useCategoriesContext();
  const { companies } = useCompaniesContext();
  const { locations, handleLocationSearch } = useLocationsContext();
  const { employees, handleEmployeeSearch } = useEmployeesContext();
  const { invoices, handleInvoicesSearch } = useInvoicesContext();
  const {
    onSearchQueryChange,
    onTypeFilterChange,
    onCategoriesFilterChange,
    onCompanyFilterChange,
    onLocationFilterChange,
    onEmployeeFilterChange,
    onInvoicesFilterChange,
    onAssignedFilterChange,
    onShowTemporaryImportFilterChange,
    onOwnershipFilterChange,
  } = useItemsFilters();

  const inventoryTypeOptions = useMemo(
    () => [
      { name: common.SMALL_INVNENTORY, value: "smallInventory" },
      { name: common.FIXED_ASSET, value: "fixedAsset" },
      { name: common.IMMATERIAL_ASSET, value: "immaterialAsset" },
    ],
    []
  );

  useEffect(() => () => handleCategoriesSearch(""), [handleCategoriesSearch]);

  const toggleFiltersHandler = () => {
    setShowFilters(!showFilters);
  };

  const closeFiltersHandler = () => {
    setShowFilters(false);
  };

  const clearFiltersHandler = () => {
    clearFilters();
    clearSorting();
    setClearChoices(true);
  };

  const updateClearHandler = () => {
    setClearChoices(false);
  };

  const applyFiltersHandler = () => {
    applyPendingFilters();
    applySortingHandler();
    closeFiltersHandler();
  };

  useEffect(() => {
    if (showFilters) return;
    applyPendingFilters();
  }, [showFilters, applyPendingFilters]);

  return (
    <div className="filters">
      <div className="filters__search">
        <SearchBar
          onChange={onSearchQueryChange}
          placeholder="Pretraži"
          autoComplete="off"
          spellCheck="false"
          id="searchQuery"
          defaultValue={applyFilters.searchTerm}
        ></SearchBar>
        <button
          className={clsx("filters__btn", { "filters__btn--active": showFilters })}
          onClick={toggleFiltersHandler}
        >
          <BsFilter />
        </button>
      </div>
      <div className={clsx("filters__container", { "filters__container--active": showFilters })}>
        <div className="filters__wrap">
          <div className="filters__close">
            <Button
              buttonIcon={<IoClose className="filters__close-icon" />}
              buttonColor={"red"}
              event={closeFiltersHandler}
            />
          </div>
          <div className="filters__options">
            <div className="filters__field">
              <p className="filters__name">Tip</p>
              <Dropdown
                choices={inventoryTypeOptions}
                onChange={(type) => {
                  updateClearHandler();
                  return onTypeFilterChange(type);
                }}
                isMultipleChoice
                allSelectedLabel="Sve"
                defaultValue={selections.types}
                clearChoices={clearChoices}
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Kategorije</p>
              <Dropdown
                choices={categoriesFilter}
                onChange={(categories) => {
                  updateClearHandler();
                  return onCategoriesFilterChange(categories);
                }}
                searchId="searchCategory"
                onSearchChange={handleCategoriesSearch}
                isMultipleChoice
                allSelectedLabel="Sve kategorije"
                defaultValue={selections.categoryId}
                clearChoices={clearChoices}
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Korisnik</p>
              <Dropdown
                choices={companies}
                onChange={(companies) => {
                  updateClearHandler();
                  return onCompanyFilterChange(companies);
                }}
                allSelectedLabel="Sve kompanije"
                defaultValue={selections.companyId}
                clearChoices={clearChoices}
                isMultipleChoice
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Vlasnik</p>
              <Dropdown
                choices={companies}
                onChange={(companies) => {
                  updateClearHandler();
                  return onOwnershipFilterChange(companies);
                }}
                allSelectedLabel="Sve kompanije"
                defaultValue={selections.ownerId}
                clearChoices={clearChoices}
                isMultipleChoice
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Lokacije</p>
              <Dropdown
                choices={locations}
                onChange={(locations) => {
                  updateClearHandler();
                  return onLocationFilterChange(locations);
                }}
                isMultipleChoice
                allSelectedLabel="Sve lokacije"
                defaultValue={selections.locationId}
                searchId="searchLocation"
                onSearchChange={handleLocationSearch}
                clearChoices={clearChoices}
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Kolege</p>
              <Dropdown
                choices={employees}
                onChange={(employees) => {
                  updateClearHandler();
                  return onEmployeeFilterChange(employees);
                }}
                isMultipleChoice
                allSelectedLabel="Sve kolege"
                defaultValue={selections.employeeEmails}
                searchId="searchEmployees"
                onSearchChange={handleEmployeeSearch}
                clearChoices={clearChoices}
              />
            </div>
            <div className="filters__field">
              <p className="filters__name">Fakture</p>
              <Dropdown
                choices={invoices}
                onChange={(invoices) => {
                  updateClearHandler();
                  return onInvoicesFilterChange(invoices);
                }}
                isMultipleChoice
                allSelectedLabel="Sve fakture"
                defaultValue={selections.invoiceId}
                searchId="searchInvoices"
                onSearchChange={handleInvoicesSearch}
                clearChoices={clearChoices}
              />
            </div>
            <div className="filters__sort-wrap">
              <SortingGroup
                clearChoices={clearChoices}
                onSelection={updateClearHandler}
                sortingOptions={sortingOptions}
              />
            </div>
          </div>
          <div className="filters__bottom">
            <div className="filters__switches">
              {!hideUnassignedSwitch ? (
                <div className="filters__field">
                  <p className="filters__name">Prikaži neraspoređena sredstva</p>
                  <InputSwitch
                    name="assignToFilter"
                    isChecked={!!applyFilters.isAssigned}
                    onChange={onAssignedFilterChange}
                  ></InputSwitch>
                </div>
              ) : null}
              <div className="filters__field">
                <p className="filters__name">Prikaži PU stavke</p>
                <InputSwitch
                  name="showTemporaryImportFilter"
                  isChecked={!!applyFilters.showTemporaryImport}
                  onChange={onShowTemporaryImportFilterChange}
                ></InputSwitch>
              </div>
            </div>
            <div className="filters__ctas">
              <Button buttonText={"Potvrdi"} buttonColor={"blue"} event={applyFiltersHandler} />
              <Button buttonText={"Poništi"} buttonColor={"red"} event={clearFiltersHandler} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
